/**
 * /instances/:id - Single orboto instance detail (OCP-204).
 *
 * Read-only customer-safe view of one tenant. Status, region, billing
 * window, storage quota, links to open the workspace. Live provisioning
 * progress while status=provisioning lands in OCP-205.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { CustomDomainCard } from '../components/CustomDomainCard';
import { LicenseGrantCard } from '../components/LicenseGrantCard';
import { ProvisioningProgress } from '../components/ProvisioningProgress';
import { ApiError, api } from '../lib/api';
import { fmtDate } from '../lib/format-time';

interface Instance {
  id: string;
  subdomain: string;
  custom_domain: string | null;
  status: string;
  subscription_state: string;
  region: string;
  license_expires_at: string | null;
  pool_tag: string;
  storage_quota_gb: number;
  created_at: string;
  paused_at: string | null;
  tenant_url: string;
  is_provisioning: boolean;
  /** OCP-593 - the language operator notices about this instance are sent in. */
  locale: string;
  /** OCP-643 - the instance login is a separate account; true = instance password not set yet, null = not reported. */
  admin_password_pending?: boolean | null;
  /** OCP-643 - the welcome link can be re-sent from here. */
  setup_link_resend_available?: boolean;
  /** OCP-850 - when the workspace was deleted after the export window (null = not deleted). */
  deleted_at?: string | null;
  /** OCP-850 - a deletion record exists / this user (the account owner) may download it. */
  deletion_record_available?: boolean;
  deletion_record_downloadable?: boolean;
}

/** Badge colours per status; the label is `instance-detail:status.<key>` (OCP-656). */
const STATUS_BADGE: Record<string, string> = {
  provisioning: 'bg-sky-100 text-sky-800',
  active: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  past_due: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200',
  paused: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  archived: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  // OCP-850 - deleted after the export window.
  deleted: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
};

export function InstanceDetailPage() {
  const { t } = useTranslation('instance-detail');
  const { id } = useParams<{ id: string }>();
  const [instance, setInstance] = useState<Instance | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(
    null,
  );

  const loadInstance = useCallback(
    async (signal?: AbortSignal) => {
      if (!id) return;
      try {
        const r = await api<Instance>(`/v1/portal/instances/${id}`, { signal });
        setInstance(r);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError)
          setError({
            status: err.status,
            message:
              err.status === 404
                ? t('notFound')
                : t('loadFailedHttp', { status: err.status }),
          });
        else setError({ status: 0, message: t('loadFailed') });
      }
    },
    [id, t],
  );

  useEffect(() => {
    const ac = new AbortController();
    void loadInstance(ac.signal);
    return () => ac.abort();
  }, [loadInstance]);

  // OCP-205: when ProvisioningProgress reports terminal, refresh the
  // instance object so the page transitions from "Provisioning" banner
  // to "Open workspace" CTA without a manual page reload.
  const onProvisioningTerminal = useCallback(() => {
    void loadInstance();
  }, [loadInstance]);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4">
        <Link
          to="/instances"
          className="text-sm text-brand-700 hover:underline"
        >
          &larr; {t('back')}
        </Link>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {error.message}
        </div>
      )}

      {!instance && !error && (
        <p className="text-sm text-gray-500">{t('loading')}</p>
      )}

      {instance && (
        <InstanceDetailBody
          instance={instance}
          onProvisioningTerminal={onProvisioningTerminal}
          onInstanceChanged={setInstance}
        />
      )}
    </div>
  );
}

function InstanceDetailBody({
  instance,
  onProvisioningTerminal,
  onInstanceChanged,
}: {
  instance: Instance;
  onProvisioningTerminal: () => void;
  /** OCP-593 - the locale control writes back through here. */
  onInstanceChanged: (i: Instance) => void;
}) {
  const { t } = useTranslation('instance-detail');
  const deleted = !!instance.deleted_at;
  const statusKey = deleted ? 'deleted' : instance.status;
  const badge = {
    label: statusKey in STATUS_BADGE || deleted ? t(`status.${statusKey}`) : instance.status,
    cls: STATUS_BADGE[statusKey] ?? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  };
  const displayHost = instance.custom_domain ?? `${instance.subdomain}.orbo.to`;
  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1
            data-testid="instance-detail-host"
            className="text-2xl font-semibold tracking-tight"
          >
            {displayHost}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {t('header.workspaceIn', { region: instance.region })}
            {instance.custom_domain
              ? t('header.customDomainNote', { subdomain: instance.subdomain })
              : ''}
            .
          </p>
        </div>
        <span
          data-testid="instance-detail-status"
          className={`rounded px-2.5 py-1 text-xs font-medium ${badge.cls}`}
        >
          {badge.label}
        </span>
      </div>

      {deleted && <DeletionRecordPanel instance={instance} />}

      {instance.is_provisioning && (
        <div className="mb-4">
          <ProvisioningProgress
            instanceId={instance.id}
            onTerminal={onProvisioningTerminal}
          />
        </div>
      )}

      {!instance.is_provisioning && instance.status !== 'archived' && (
        <div className="mb-4">
          <a
            href={instance.tenant_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            {t('open.cta', { host: displayHost })}
            <span aria-hidden>&rarr;</span>
          </a>
          {/* OCP-643 - the workspace login is a separate account from this portal. */}
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-400" data-testid="separate-login-hint">
            {t('open.separateLogin')}
          </p>
          {instance.admin_password_pending === true && (
            <SetupLinkNotice instance={instance} />
          )}
        </div>
      )}

      <dl className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 sm:grid-cols-2">
        <Field label={t('field.subdomain')} value={`${instance.subdomain}.orbo.to`} />
        <Field
          label={t('field.customDomain')}
          value={instance.custom_domain ?? '-'}
        />
        <Field label={t('field.region')} value={instance.region} />
        <Field label={t('field.pool')} value={instance.pool_tag} />
        <Field
          label={t('field.storageQuota')}
          value={t('field.storageValue', { gb: instance.storage_quota_gb })}
        />
        <Field
          label={t('field.subscriptionState')}
          value={instance.subscription_state}
        />
        <Field
          label={t('field.licenseEnds')}
          value={
            instance.license_expires_at
              ? formatDate(instance.license_expires_at)
              : t('field.lifetime')
          }
        />
        {!deleted && <LocaleField instance={instance} onSaved={onInstanceChanged} />}
        <Field label={t('field.created')} value={formatDate(instance.created_at)} />
        {/* OCP-630 - the same monthly availability number the operator sees. */}
        {!instance.is_provisioning && instance.status !== 'archived' && <AvailabilityField instanceId={instance.id} />}
        {instance.paused_at && (
          <Field label={t('field.pausedAt')} value={formatDate(instance.paused_at)} />
        )}
      </dl>

      {/* OCP-911 / OCP-917 - the plan from the code: end, grace, shutdown + export window, and the
          switch of THIS workspace to a subscription (reminder mails link here
          with ?convert=1). Renders nothing for a workspace not from a code. */}
      {!deleted && !instance.is_provisioning && <LicenseGrantCard instanceId={instance.id} />}

      {/* OCP-286: custom-domain management for live instances. Hidden
          while provisioning (no stack yet) and for archived tenants. */}
      {!instance.is_provisioning && instance.status !== 'archived' && (
        <CustomDomainCard instanceId={instance.id} />
      )}
    </>
  );
}

/**
 * OCP-850 - a deleted workspace: when it was deleted, that no copy is kept,
 * and the deletion record (AVV section 7 (2)) as a PDF for the account
 * owner. The PDF comes in the account language.
 */
function DeletionRecordPanel({ instance }: { instance: Instance }) {
  const { t } = useTranslation('instance-detail');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function download() {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/v1/portal/instances/${encodeURIComponent(instance.id)}/deletion-record.pdf`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const match = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '');
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = match?.[1] ?? `deletion-record-${instance.subdomain}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setError(t('deletion.downloadFailed'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      data-testid="deletion-record-panel"
      className="mb-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 p-4 text-sm text-gray-800 dark:text-gray-200"
    >
      <p className="font-medium">{t('deletion.title')}</p>
      <p className="mt-1">{t('deletion.body', { date: formatDate(instance.deleted_at ?? null) })}</p>
      {instance.deletion_record_downloadable && (
        <button
          type="button"
          onClick={() => void download()}
          disabled={busy}
          data-testid="deletion-record-download"
          className="mt-3 rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {busy ? t('deletion.downloading') : t('deletion.download')}
        </button>
      )}
      {instance.deletion_record_available && !instance.deletion_record_downloadable && (
        <p data-testid="deletion-record-owner-only" className="mt-2 text-xs text-gray-600 dark:text-gray-400">
          {t('deletion.ownerOnly')}
        </p>
      )}
      {!instance.deletion_record_available && (
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{t('deletion.noRecord')}</p>
      )}
      {error && <p role="alert" className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p>}
    </div>
  );
}

/** OCP-630 - this month's availability from the operator's minute probe (one vantage point). */
function AvailabilityField({ instanceId }: { instanceId: string }) {
  const { t } = useTranslation('instance-detail');
  const [text, setText] = useState<string>(t('availability.loading'));
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const a = await api<{ month: string; closed: boolean; availability_pct: number; threshold_pct: number; minutes_excluded: number; incidents: unknown[] }>(
          `/v1/portal/instances/${instanceId}/availability`,
        );
        if (cancelled) return;
        if (!a || typeof a.availability_pct !== 'number' || !Array.isArray(a.incidents)) {
          setText(t('availability.couldNotLoad'));
          return;
        }
        setText(
          t('availability.summary', {
            pct: a.availability_pct.toFixed(2),
            threshold: a.threshold_pct,
            outages: a.incidents.length ? t('availability.outages', { count: a.incidents.length }) : t('availability.noOutages'),
            excluded: a.minutes_excluded ? t('availability.excluded', { minutes: a.minutes_excluded }) : '',
          }),
        );
      } catch (err) {
        if (cancelled) return;
        setText(err instanceof ApiError && (err.status === 503 || err.status === 409) ? t('availability.notMeasured') : t('availability.couldNotLoad'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instanceId, t]);
  return (
    <div data-testid="instance-availability">
      <dt className="text-xs uppercase tracking-wide text-gray-500">{t('availability.label')}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{text}</dd>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );
}

function formatDate(iso: string | null): string {
  return fmtDate(iso, '-');
}

/**
 * OCP-593 - the communication locale for operator mails about this instance
 * (maintenance notices, all-clears). Owner/admin can change it; the API
 * refuses other roles with 403.
 */
/**
 * OCP-643 - shown while the workspace admin has not set the workspace
 * password: explains the separate login and re-sends the welcome link
 * (one send per 15 minutes, never rotates - the operator path does that).
 */
function SetupLinkNotice({ instance }: { instance: Instance }) {
  const { t } = useTranslation('instance-detail');
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function resend() {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ sent: true; to: string }>(`/v1/portal/instances/${instance.id}/setup-link`, { method: 'POST' });
      setSentTo(r.to);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const code = err instanceof ApiError ? (err.body as { error?: string } | null)?.error : undefined;
      setError(
        status === 429
          ? t('setupLink.rateLimited')
          : status === 403
            ? t('setupLink.forbidden')
            : status === 409 && code === 'deploy_in_flight'
              ? t('setupLink.deployInFlight') // OCP-870 - the workspace is mid-deploy
              : status === 409
                ? t('setupLink.alreadySet')
                : t('setupLink.failed'),
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      data-testid="setup-link-notice"
      className="mt-3 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-3 text-sm text-amber-900 dark:text-amber-200"
    >
      <p className="font-medium">{t('setupLink.title')}</p>
      <p className="mt-1 text-xs">{t('setupLink.body')}</p>
      {instance.setup_link_resend_available && !sentTo && (
        <button
          type="button"
          onClick={() => void resend()}
          disabled={busy}
          data-testid="setup-link-resend"
          className="mt-2 rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
        >
          {busy ? t('setupLink.sending') : t('setupLink.resend')}
        </button>
      )}
      {sentTo && <p className="mt-2 text-xs">{t('setupLink.sentTo', { to: sentTo })}</p>}
      {error && <p role="alert" className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p>}
    </div>
  );
}

function LocaleField({ instance, onSaved }: { instance: Instance; onSaved: (i: Instance) => void }) {
  const { t } = useTranslation('instance-detail');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function change(locale: string) {
    setSaving(true);
    setError(null);
    try {
      const r = await api<Instance>(`/v1/portal/instances/${instance.id}/locale`, { method: 'PATCH', body: { locale } });
      onSaved(r);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? t('locale.forbidden') : t('locale.failed'));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('locale.label')}</dt>
      <dd className="mt-1 text-sm">
        <select
          value={instance.locale === 'de' ? 'de' : 'en'}
          onChange={(e) => void change(e.target.value)}
          disabled={saving}
          data-testid="instance-locale"
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
        >
          <option value="en">English</option>
          <option value="de">Deutsch</option>
        </select>
        <span className="ml-2 text-xs text-gray-500">{t('locale.hint')}</span>
        {error && <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p>}
      </dd>
    </div>
  );
}
